import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { assertNonEmptyString, assertOneOf } from "../../internal/contracts";
import { useThemeA } from "../../internal/ThemeAContext";
import { BUTTON_ICON_CLASS, BUTTON_LABEL_CLASS, buttonClassName, iconActionClassName } from "./buttonStyles";

// Largeur du panneau (en px) : sous ce seuil, le menu s'ouvre vers la droite,
// son bouton étant trop près du bord gauche pour qu'il tienne aligné à droite.
const MENU_WIDTH_PX = { default: 256, compact: 240 };
const MENU_GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 8;
// Hauteur minimale d'un panneau borné : il reste utilisable même quand le
// bouton touche presque le bord de l'écran.
const MIN_SCROLLING_MENU_HEIGHT_PX = 120;
const SIZES = ["default", "compact"];

const ITEM_CLASS = [
  "akv:flex akv:w-full akv:min-h-10 akv:items-center akv:gap-2.5 akv:rounded-ak-control akv:border-0",
  "akv:bg-transparent akv:px-3 akv:py-2 akv:text-left akv:font-ak-body akv:text-sm akv:font-semibold",
  "akv:cursor-pointer akv:outline-none akv:transition-colors akv:duration-150 akv:motion-reduce:transition-none",
  "akv:disabled:cursor-not-allowed akv:disabled:opacity-[0.58]",
].join(" ");
const ITEM_TONE = {
  default: "akv:text-ak-text akv:hover:bg-ak-subtle akv:focus-visible:bg-ak-subtle",
  destructive: "akv:text-ak-critical akv:hover:bg-ak-critical-soft akv:focus-visible:bg-ak-critical-soft",
};
// Menu de ligne (`size="compact"`) : bouton détouré, lisible sur une ligne de
// tableau comme sur une carte, sans dépendre de l'habillage d'un en-tête.
const COMPACT_TRIGGER_CLASS = "akv:bg-ak-surface! akv:text-ak-text! akv:ring-1 akv:ring-inset akv:ring-ak-border-strong akv:shadow-none! akv:hover:bg-ak-brand-soft! akv:hover:text-ak-brand-hover!";
const GROUP_LABEL_CLASS = "akv:block akv:px-3 akv:pb-1 akv:pt-2 akv:font-ak-body akv:text-[0.6875rem] akv:font-bold akv:uppercase akv:tracking-[0.08em] akv:text-ak-muted";
// Entrée de niveau 1 sans geste propre : intitulé de ses entrées de niveau 2.
const PARENT_LABEL_CLASS = "akv:flex akv:min-h-10 akv:w-full akv:items-center akv:gap-2.5 akv:px-3 akv:py-2 akv:font-ak-body akv:text-sm akv:font-semibold akv:text-ak-text";
// Entrée de niveau 2 : en retrait, filet vertical à gauche, texte plus petit et plus discret.
const CHILD_ITEM_CLASS = [
  "akv:relative akv:flex akv:w-full akv:min-h-9 akv:items-center akv:rounded-ak-control akv:border-0",
  "akv:bg-transparent akv:py-1.5 akv:pl-8 akv:pr-3 akv:text-left akv:font-ak-body akv:text-[0.8125rem] akv:font-normal akv:text-ak-muted",
  "akv:cursor-pointer akv:outline-none akv:transition-colors akv:duration-150 akv:motion-reduce:transition-none",
  "akv:hover:bg-ak-subtle akv:hover:text-ak-text akv:focus-visible:bg-ak-subtle akv:focus-visible:text-ak-text",
  "akv:disabled:cursor-not-allowed akv:disabled:opacity-[0.58]",
  "akv:before:absolute akv:before:inset-y-0 akv:before:left-[1.125rem] akv:before:w-px akv:before:bg-ak-border akv:before:content-['']",
].join(" ");
const TRIGGER_INTENTS = ["primary", "secondary"];

const ChevronIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="akv:h-4 akv:w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// Actions atteignables au clavier : ni désactivées, ni masquées (actions
// courantes réservées à l'écran étroit).
const isWideScreen = () => (typeof window !== "undefined" && typeof window.matchMedia === "function"
  ? window.matchMedia("(min-width: 40rem)").matches
  : true);
const menuItems = (menu) => Array.from(menu?.querySelectorAll('[role="menuitem"]') || [])
  .filter((node) => !node.disabled && !(node.closest("[data-ak-v2-compact-only]") && isWideScreen()));

const DotsIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="akv:h-[1.125rem] akv:w-[1.125rem]" fill="currentColor">
    <circle cx="5" cy="12" r="1.8" />
    <circle cx="12" cy="12" r="1.8" />
    <circle cx="19" cy="12" r="1.8" />
  </svg>
);

/**
 * Menu « ••• » des actions d'une fiche : tout ce qui n'est ni l'action
 * principale ni l'une des deux actions courantes, rangé par groupes nommés
 * (Statut, Aller à, Outils…). Règle commune aux fiches V2 : une action
 * destructive (`intent: "destructive"`) n'est jamais un bouton visible ; le
 * menu la place toujours en dernier, après un séparateur, quel que soit son
 * groupe d'origine.
 *
 * `compactItems` : actions courantes que l'en-tête masque sur écran étroit ;
 * elles n'apparaissent dans le menu que sous 40rem.
 *
 * `size="compact"` : menu d'une ligne de liste ou d'une carte (bouton détouré,
 * panneau plus étroit). Le panneau est rendu hors du flux, en position fixe,
 * pour ne jamais être rogné par un tableau qui défile ; il s'ouvre vers le
 * haut quand la place manque en bas de l'écran.
 *
 * Panneau plus haut que la place disponible des deux côtés (menu d'un groupe
 * d'évaluation rapide sur un écran d'ordinateur portable, GitLab #1231) : il
 * s'ouvre du côté le plus grand, borné au bord de l'écran, et défile en
 * lui-même ; toutes ses entrées restent atteignables, à la souris comme au
 * clavier (l'entrée qui prend le focus défile dans la vue).
 *
 * Niveaux (retour 38 du 26/10/2026) : une entrée peut porter `children`, ses
 * entrées de niveau 2, toujours visibles sous elle, en retrait ; les flèches
 * parcourent les deux niveaux à plat. Sans `onSelect`, l'entrée de niveau 1
 * n'est qu'un intitulé.
 *
 * `triggerLabel` : le déclencheur devient un bouton libellé avec chevron
 * (« + Nouveau ▾ ») au lieu des points ; `label` nomme alors le panneau.
 */
export function ActionMenu({ compactItems = [], groups = [], label, size = "default", triggerIntent = "primary", triggerLabel = null }) {
  useThemeA("ActionMenu");
  assertNonEmptyString("ActionMenu", "label", label);
  assertOneOf("ActionMenu", "size", size, SIZES);
  assertOneOf("ActionMenu", "triggerIntent", triggerIntent, TRIGGER_INTENTS);
  const menuId = useId();
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  // Les actions destructives quittent leur groupe pour la zone de fin.
  const visibleGroups = groups
    .map((group) => ({ ...group, items: (group.items || []).filter((item) => item.intent !== "destructive") }))
    .filter((group) => group.items.length);
  const dangerItems = groups.flatMap((group) => (group.items || []).filter((item) => item.intent === "destructive"));
  const hasWideItems = visibleGroups.length > 0 || dangerItems.length > 0;
  const hasItems = hasWideItems || compactItems.length > 0;

  const focusItem = (index) => {
    const list = menuItems(menuRef.current);
    if (!list.length) return;
    list[(index + list.length) % list.length].focus();
  };
  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    // Ouvert : focus sur la première action, fermeture au clic extérieur.
    menuItems(menuRef.current)[0]?.focus();
    const onPointerDown = (event) => {
      if (menuRef.current?.contains(event.target) || triggerRef.current?.contains(event.target)) return;
      close(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, close]);

  // Position fixe calculée depuis le bouton : alignée à droite (à gauche près du
  // bord), sous le bouton, ou au-dessus si la place manque en bas de l'écran.
  // Trop haut pour l'un comme l'autre côté : du côté le plus grand, borné à
  // l'écran (`maxHeight`), et le panneau défile.
  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = MENU_WIDTH_PX[size];
    // Hauteur du contenu entier, même quand le panneau est déjà borné et défile.
    const height = menuRef.current?.scrollHeight || 0;
    const viewportHeight = window.innerHeight || 0;
    const alignStart = rect.right < width;
    const below = rect.bottom + MENU_GAP_PX;
    const roomBelow = viewportHeight - VIEWPORT_MARGIN_PX - below;
    const roomAbove = rect.top - MENU_GAP_PX - VIEWPORT_MARGIN_PX;
    const opensUp = height > 0 && height > roomBelow && (height <= roomAbove || roomAbove > roomBelow);
    const room = opensUp ? roomAbove : roomBelow;
    const maxHeight = height > 0 && height > room ? Math.max(room, MIN_SCROLLING_MENU_HEIGHT_PX) : null;
    const shownHeight = maxHeight ?? height;
    const next = {
      left: alignStart ? rect.left : Math.max(VIEWPORT_MARGIN_PX, rect.right - width),
      top: opensUp ? rect.top - MENU_GAP_PX - shownHeight : below,
      maxHeight,
      placement: opensUp ? "top" : "bottom",
    };
    setPosition((current) => (current
      && current.left === next.left && current.top === next.top
      && current.maxHeight === next.maxHeight && current.placement === next.placement ? current : next));
  }, [size]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    // Le défilement du panneau lui-même ne déplace pas le bouton.
    const onScroll = (event) => {
      if (event?.target instanceof Node && menuRef.current?.contains(event.target)) return;
      place();
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, place]);

  if (!hasItems) return null;

  const toggle = () => {
    // Position provisoire sous le bouton dès l'ouverture : le panneau est
    // visible (donc focalisable) au premier rendu, puis l'effet le retourne
    // vers le haut si sa hauteur réelle ne tient pas en bas de l'écran.
    if (!open) place();
    setOpen((value) => !value);
  };
  const onMenuKeyDown = (event) => {
    const list = menuItems(menuRef.current);
    const index = list.indexOf(document.activeElement);
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      focusItem(index + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusItem(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusItem(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusItem(list.length - 1);
    } else if (event.key === "Tab") {
      // Le panneau vit hors du flux : le focus revient au bouton, puis la
      // tabulation continue normalement depuis lui.
      close();
    }
  };
  const select = (item) => {
    close();
    item.onSelect?.();
  };
  const itemIcon = (item) => (item.icon ? <span className={`${BUTTON_ICON_CLASS} akv:w-4`} aria-hidden="true">{item.icon}</span> : null);
  const renderChild = (child) => (
    <button
      key={child.id}
      className={CHILD_ITEM_CLASS}
      data-ak-v2-action-id={child.id}
      data-ak-v2-menu-level="2"
      disabled={child.disabled}
      onClick={() => select(child)}
      role="menuitem"
      tabIndex={-1}
      type="button"
    >
      <span className="akv:min-w-0 akv:flex-1">{child.label}</span>
    </button>
  );
  const renderItem = (item) => {
    const children = item.children || [];
    const parent = !item.onSelect && children.length ? (
      <span className={PARENT_LABEL_CLASS} data-ak-v2-action-id={item.id} data-ak-v2-menu-level="1" key={item.id}>
        {itemIcon(item)}
        <span className="akv:min-w-0 akv:flex-1">{item.label}</span>
      </span>
    ) : (
      <button
        key={item.id}
        className={`${ITEM_CLASS} ${ITEM_TONE[item.intent === "destructive" ? "destructive" : "default"]}`}
        data-ak-v2-action-id={item.id}
        data-ak-v2-menu-level={children.length ? "1" : undefined}
        disabled={item.disabled}
        onClick={() => select(item)}
        role="menuitem"
        tabIndex={-1}
        type="button"
      >
        {itemIcon(item)}
        <span className="akv:min-w-0 akv:flex-1">{item.label}</span>
      </button>
    );
    if (!children.length) return parent;
    return <React.Fragment key={item.id}>{parent}{children.map(renderChild)}</React.Fragment>;
  };

  return (
    <span
      className={`akv:relative akv:inline-flex akv:flex-none ${hasWideItems ? "" : "akv:min-[40rem]:hidden"}`.trim()}
      data-ak-v2-component="action-menu"
      data-ak-v2-size={size}
    >
      <button
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={triggerLabel ? undefined : label}
        className={triggerLabel
          ? buttonClassName(triggerIntent)
          : `${iconActionClassName("quiet")} ${size === "compact" ? COMPACT_TRIGGER_CLASS : ""}`.trim()}
        data-ak-v2-intent={triggerLabel ? triggerIntent : "quiet"}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" && !open) {
            event.preventDefault();
            toggle();
          }
        }}
        ref={triggerRef}
        title={triggerLabel ? undefined : label}
        type="button"
      >
        {triggerLabel ? (
          <>
            <span className={BUTTON_LABEL_CLASS}>{triggerLabel}</span>
            <span className={`${BUTTON_ICON_CLASS} akv:transition-transform akv:motion-reduce:transition-none ${open ? "akv:rotate-180" : ""}`.trim()} aria-hidden="true"><ChevronIcon /></span>
          </>
        ) : (
          <span className={BUTTON_ICON_CLASS} aria-hidden="true"><DotsIcon /></span>
        )}
      </button>
      {open && typeof document !== "undefined" ? createPortal(
        <div
          aria-label={label}
          className={`ak-v2-portal-theme akv:fixed akv:z-[1450] akv:box-border akv:flex akv:max-w-[calc(100vw-1rem)] akv:flex-col akv:gap-0.5 akv:overflow-x-hidden akv:overflow-y-auto akv:overscroll-contain akv:rounded-ak-control akv:bg-ak-surface akv:p-1.5 akv:font-ak-body akv:shadow-ak-overlay ${size === "compact" ? "akv:w-60" : "akv:w-64"}`}
          data-akongo-ui="v2"
          data-ak-v2-component="action-menu-panel"
          data-ak-v2-placement={position?.placement || "bottom"}
          data-ak-v2-scrolls={position?.maxHeight != null ? "" : undefined}
          id={menuId}
          onKeyDown={onMenuKeyDown}
          ref={menuRef}
          role="menu"
          // `maxHeight` en style en ligne : les utilitaires `akv:` sont `!important`.
          style={position ? { left: position.left, top: position.top, ...(position.maxHeight != null ? { maxHeight: position.maxHeight } : {}) } : undefined}
        >
          {compactItems.length ? (
            <div className="akv:flex akv:flex-col akv:gap-0.5 akv:min-[40rem]:hidden" data-ak-v2-compact-only="" role="group">
              {compactItems.map((item) => renderItem(item))}
            </div>
          ) : null}
          {visibleGroups.map((group) => (
            <div key={group.id} className="akv:flex akv:flex-col akv:gap-0.5" role="group" aria-label={group.label || undefined}>
              {group.label ? <span className={GROUP_LABEL_CLASS} aria-hidden="true">{group.label}</span> : null}
              {group.items.map((item) => renderItem(item))}
            </div>
          ))}
          {dangerItems.length ? (
            <>
              {visibleGroups.length || compactItems.length ? <span className="akv:mx-1.5 akv:my-1 akv:block akv:h-px akv:flex-none akv:bg-ak-border" role="separator" /> : null}
              <div className="akv:flex akv:flex-col akv:gap-0.5" role="group">
                {dangerItems.map((item) => renderItem(item))}
              </div>
            </>
          ) : null}
        </div>,
        document.body
      ) : null}
    </span>
  );
}

const itemShape = PropTypes.shape({
  // Entrées de niveau 2, toujours visibles sous celle-ci (jamais destructives).
  children: PropTypes.arrayOf(PropTypes.shape({
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onSelect: PropTypes.func,
  })),
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  id: PropTypes.string.isRequired,
  intent: PropTypes.oneOf(["default", "destructive"]),
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func,
});

ActionMenu.propTypes = {
  compactItems: PropTypes.arrayOf(itemShape),
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(itemShape).isRequired,
    label: PropTypes.string,
  })),
  label: PropTypes.string.isRequired,
  size: PropTypes.oneOf(SIZES),
  triggerIntent: PropTypes.oneOf(TRIGGER_INTENTS),
  // Déclencheur libellé (« + Nouveau ») avec chevron, au lieu des points.
  triggerLabel: PropTypes.string,
};

export default ActionMenu;
